import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Link,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

import WhatsAppIconWhite from "../../assets/logoLoginWhite.png";
import WhatsAppIcon from "../../assets/logologinnovo.png";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    overflow: 'hidden',
  },

  // --- Painel Esquerdo (Marca) ---
  brandPanel: {
    flex: '0 0 45%',
    background: 'linear-gradient(160deg, #1a73d7 0%, #0d47a1 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  // Círculos decorativos de fundo
  decorCircle1: {
    position: 'absolute',
    width: 320,
    height: 320,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)',
    top: -80,
    right: -80,
  },
  decorCircle2: {
    position: 'absolute',
    width: 220,
    height: 220,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    bottom: 40,
    left: -60,
  },
  decorCircle3: {
    position: 'absolute',
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.08)',
    bottom: 180,
    right: 40,
  },
  brandContent: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    maxWidth: 360,
  },
  brandLogo: {
    height: 72,
    width: 'auto',
    marginBottom: theme.spacing(4),
    filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.2))',
  },
  brandTagline: {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '1.75rem',
    lineHeight: 1.3,
    marginBottom: theme.spacing(2),
  },
  brandSubtitle: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.95rem',
    lineHeight: 1.6,
  },
  brandDivider: {
    width: 48,
    height: 3,
    background: 'rgba(255,255,255,0.4)',
    borderRadius: 2,
    margin: `${theme.spacing(3)}px auto`,
  },

  // --- Painel Direito (Formulário) ---
  formPanel: {
    flex: '0 0 55%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 100%',
    },
  },
  formWrapper: {
    width: '100%',
    maxWidth: 400,
  },
  formHeader: {
    marginBottom: theme.spacing(4),
  },
  formTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  formSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  // Logo visível só no mobile
  mobileLogo: {
    display: 'none',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  mobileLogoImg: {
    height: 48,
    width: 'auto',
  },
  form: {
    width: '100%',
  },
  textField: {
    marginBottom: theme.spacing(0.5),
  },
  forgotLink: {
    display: 'block',
    textAlign: 'right',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    fontSize: '0.82rem',
    fontWeight: 500,
  },
  submitButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    fontWeight: 600,
    fontSize: '1rem',
    borderRadius: theme.spacing(1),
    boxShadow: '0px 4px 14px rgba(26, 115, 215, 0.4)',
    textTransform: 'none',
  },
  registerRow: {
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  registerLink: {
    fontWeight: 500,
    fontSize: '0.875rem',
  },
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDarkMode = theme.palette.type === 'dark';
  const [user, setUser] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setUser({
      ...user,
      [name]: name === "email" ? value.toLowerCase() : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await handleLogin(user);
    } catch (err) {
      toast.error("Erro ao fazer login. Verifique suas credenciais.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("expired") === "true") {
      if (!toast.isActive("session-expired")) {
        toast.warn("Sessão expirada. Por favor, faça login novamente.", {
          toastId: "session-expired",
        });
      }
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete("expired");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  return (
    <div className={classes.root}>
      <CssBaseline />

      {/* Painel Esquerdo — Marca */}
      <div className={classes.brandPanel}>
        <div className={classes.decorCircle1} />
        <div className={classes.decorCircle2} />
        <div className={classes.decorCircle3} />

        <div className={classes.brandContent}>
          <img
            src={WhatsAppIconWhite}
            alt="Message Express"
            className={classes.brandLogo}
          />

          <div className={classes.brandDivider} />

          <Typography variant="h5" className={classes.brandTagline}>
            Atendimento inteligente, resultados reais.
          </Typography>

          <Typography variant="body2" className={classes.brandSubtitle}>
            Gerencie conversas, equipes e campanhas em um só lugar — com a agilidade que o seu negócio precisa.
          </Typography>
        </div>
      </div>

      {/* Painel Direito — Formulário */}
      <div className={classes.formPanel}>
        <div className={classes.formWrapper}>

          {/* Logo apenas no mobile */}
          <div className={classes.mobileLogo}>
            <img
              src={isDarkMode ? WhatsAppIconWhite : WhatsAppIcon}
              alt="Message Express"
              className={classes.mobileLogoImg}
            />
          </div>

          <div className={classes.formHeader}>
            <Typography variant="h5" className={classes.formTitle}>
              Bem-vindo de volta
            </Typography>
            <Typography variant="body2" className={classes.formSubtitle}>
              Faça login para acessar sua conta
            </Typography>
          </div>

          <form className={classes.form} noValidate onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              fullWidth
              id="email"
              label={i18n.t('login.form.email')}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              required
              className={classes.textField}
            />

            <TextField
              variant="outlined"
              fullWidth
              name="password"
              label={i18n.t('login.form.password')}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              required
              style={{ marginTop: 16 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              color="primary"
              className={classes.forgotLink}
            >
              Esqueci minha senha
            </Link>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submitButton}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? i18n.t('login.buttons.loading') : i18n.t('login.buttons.submit')}
            </Button>

            <Grid container justifyContent="center" className={classes.registerRow}>
              <Grid item>
                <Link
                  component={RouterLink}
                  to="/signup"
                  variant="body2"
                  className={classes.registerLink}
                >
                  {i18n.t('login.buttons.register')}
                </Link>
              </Grid>
            </Grid>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
